// Dates on the memory pages. A time is shown in UTC and says so: a console used in two time zones must
// not show two different days for one confirmation. The clock is passed in, so a test fixes it.

const DAY_MS = 86_400_000;

const dateFormat = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "short",
  year: "numeric",
  timeZone: "UTC",
});
const timeFormat = new Intl.DateTimeFormat("en-GB", {
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
  timeZone: "UTC",
});

export function formatDate(instant: string): string {
  const date = new Date(instant);
  return Number.isNaN(date.getTime()) ? instant : dateFormat.format(date);
}

export function formatDateTime(instant: string): string {
  const date = new Date(instant);
  if (Number.isNaN(date.getTime())) return instant;
  return `${dateFormat.format(date)}, ${timeFormat.format(date)} UTC`;
}

function wholeDaysBetween(from: Date, to: Date): number {
  return Math.floor((to.getTime() - from.getTime()) / DAY_MS);
}

function plural(count: number, unit: string): string {
  return `${count} ${unit}${count === 1 ? "" : "s"}`;
}

/** "today", "yesterday", "5 days ago", "3 months ago". */
export function agoPhrase(instant: string, now: Date): string {
  const days = wholeDaysBetween(new Date(instant), now);
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 60) return `${days} days ago`;
  if (days < 730) return `${plural(Math.floor(days / 30), "month")} ago`;
  return `${plural(Math.floor(days / 365), "year")} ago`;
}

/** What `confirmedPhrase` needs of a fact besides its last confirmation date. */
export type ConfirmationFacts = Readonly<{
  confirmationLevel: "UNCONFIRMED" | "STAFF_CONFIRMED" | "CUSTOMER_CONFIRMED";
  createdAt: string;
  createdByKind: "customer" | "staff" | "system";
  /** When the fact last changed state (an activation or a resolved dispute stamps it). */
  statusChangedAt?: string | null;
}>;

const LEVEL_WORDS = {
  STAFF_CONFIRMED: "Confirmed by staff",
  CUSTOMER_CONFIRMED: "Confirmed by the customer",
} as const;

/**
 * How fresh a fact is: when a PERSON last confirmed it. A fact nobody confirmed says so.
 *
 * A fact that a person wrote has no later confirmation date, but it was confirmed when it was added. That
 * sentence is true only when `createdByKind` is not `system`. A fact the assistant wrote and staff activated (or
 * kept after a dispute) also has `lastConfirmedAt` null and a confirmed level, but it was NOT confirmed when it was
 * added: the level changed later. It says who confirmed it, with the time of the last state change as the date
 * (`date unknown` when the API sends none).
 */
export function confirmedPhrase(
  lastConfirmedAt: string | null,
  now: Date,
  facts?: ConfirmationFacts,
): string {
  if (lastConfirmedAt !== null) {
    return `Last confirmed ${agoPhrase(lastConfirmedAt, now)} (${formatDate(lastConfirmedAt)})`;
  }
  if (facts === undefined || facts.confirmationLevel === "UNCONFIRMED") {
    return "Not confirmed by a person yet";
  }
  if (facts.createdByKind !== "system") {
    return `Confirmed when it was added (${formatDate(facts.createdAt)})`;
  }
  const date = facts.statusChangedAt ? formatDate(facts.statusChangedAt) : "date unknown";
  return `${LEVEL_WORDS[facts.confirmationLevel]} (${date})`;
}

/** When a fact lapses by itself. */
export function expiryPhrase(expiresAt: string | null, now: Date): string {
  if (expiresAt === null) return "No expiry date";
  const days = wholeDaysBetween(now, new Date(expiresAt));
  if (days < 0) return `Past its expiry date (${formatDate(expiresAt)})`;
  if (days === 0) return `Expires today (${formatDate(expiresAt)})`;
  if (days < 60) return `Expires in ${plural(days, "day")} (${formatDate(expiresAt)})`;
  return `Expires ${formatDate(expiresAt)}`;
}

/** A confidence of 0 to 1000 as a percentage. It is a hint only: it never approves a fact. */
export const confidencePercent = (permille: number): string => `${Math.round(permille / 10)}%`;
